import React from 'react';
import * as Headless from '@headlessui/react';
import clsx from 'clsx';

import { TouchTarget } from './button-tw';
import { Link } from './link';

/**
 * Configuration properties for the Avatar components.
 */
type AvatarProps = {
  /** The image source URL. */
  src?: string | null;
  /** Whether the avatar should be square instead of round. */
  square?: boolean;
  /** Initials to display when the image is not available. */
  initials?: string;
  /** Accessible text for the avatar. */
  alt?: string;
  /** Optional CSS class name. */
  className?: string;
};

/**
 * A standard avatar component for displaying user images or initials.
 */
export function AvatarTw({
  src = null,
  square = false,
  initials,
  alt = '',
  className,
  ...props
}: AvatarProps & React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="avatar"
      {...props}
      className={clsx(
        className,
        // Basic layout
        '*:col-start-1 *:row-start-1 inline-grid shrink-0 align-middle [--avatar-radius:20%] [--ring-opacity:20%]',
        'outline-black/(--ring-opacity) dark:outline-white/(--ring-opacity) outline -outline-offset-1',
        // Add the correct border radius
        square
          ? 'rounded-(--avatar-radius) *:rounded-(--avatar-radius)'
          : '*:rounded-full rounded-full'
      )}
    >
      {initials && (
        <svg
          aria-hidden={alt ? undefined : 'true'}
          className="size-full select-none fill-current p-[5%] text-[48px] font-medium uppercase"
          viewBox="0 0 100 100"
        >
          {alt && <title>{alt}</title>}
          <text
            alignmentBaseline="middle"
            dominantBaseline="middle"
            dy=".125em"
            textAnchor="middle"
            x="50%"
            y="50%"
          >
            {initials}
          </text>
        </svg>
      )}

      {src ? <img alt={alt} className="size-full" src={src} /> : null}
    </span>
  );
}

/**
 * An interactive avatar that acts as a button or a link.
 */
export function AvatarButtonTw({
  src,
  square = false,
  initials,
  alt,
  className,
  // ref,
  ...props
}: AvatarProps &
  (
    | Omit<Headless.ButtonProps, 'as' | 'className'>
    | Omit<React.ComponentProps<typeof Link>, 'className'>
  )) {
  const classes = clsx(
    className,
    square ? 'rounded-[20%]' : 'rounded-full',
    'relative inline-grid focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-primary-500'
  );

  return 'href' in props ? (
    <Link
      {...props}
      // ref={ref as React.ForwardedRef<HTMLAnchorElement>}
      className={classes}
    >
      <TouchTarget>
        <AvatarTw alt={alt} initials={initials} square={square} src={src} />
      </TouchTarget>
    </Link>
  ) : (
    <Headless.Button
      {...props}
      // ref={ref}
      className={classes}
    >
      <TouchTarget>
        <AvatarTw alt={alt} initials={initials} square={square} src={src} />
      </TouchTarget>
    </Headless.Button>
  );
}
